import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const ManaliKasol = () => {
  const tourData = {
    name: "Manali and Kasol",
    duration: "06 Nights 07 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935854/vd5hkycxxmpaejipn0ow.jpg",
    altitude: "5,180 ft",
    difficulty: "Easy",
    region: "Himachal Pradesh",
    highlights: ["Mountain towns", "Cultural experience", "Scenic beauty"],
    bestTime: "March to November",
    description: "The Manali and Kasol tour is a perfect combination of mountain towns and cultural experiences in Himachal Pradesh. This easy tour offers scenic beauty, adventure activities, and a chance to explore the vibrant culture of both Manali and Kasol."
  };

  return (
    <>
      <Helmet>
        <title>Manali and Kasol Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Manali and Kasol tour in Himachal Pradesh. Mountain towns with cultural experiences and scenic beauty." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Mountain towns with cultural experiences and scenic beauty
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you through the beautiful mountain towns of Manali and Kasol, 
                    offering a perfect blend of adventure, culture, and scenic beauty. The journey 
                    includes adventure activities in Solang Valley, cultural exploration in Manali, 
                    and the vibrant atmosphere of Kasol, often called the 'Amsterdam of India'.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Mountain towns exploration</li>
                    <li>Cultural experiences</li>
                    <li>Scenic beauty</li>
                    <li>Adventure activities</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for all ages</li>
                    <li>Photography opportunities</li>
                    <li>Local market exploration</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Overnight Journey to Manali</h4>
                    <ul>
                      <li>Your journey begins with an overnight drive from Delhi to Manali, covering approximately 540 kilometers in 12-14 hours.</li>
                      <li>This scenic route takes you from the bustling plains to the tranquil mountains, offering a preview of the natural beauty ahead.</li>
                      <li>The changing landscapes, from urban sprawls to winding hill roads, keep the journey interesting and enjoyable.</li>
                      <li>Because the drive is long, you'll have ample time to relax and prepare yourself for the adventures awaiting in Manali.</li>
                      <li>Taking short breaks during the journey will help you stay refreshed.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Arrival in Manali</h4>
                    <ul>
                      <li>Reach Manali by early morning and get yourself checked into the hotel.</li>
                      <li>Take a break and refresh enough from the previous night travel.</li>
                      <li>Go outside to discover the nearby sights including the Hadimba Devi Temple for the local historical and cultural lifestyle.</li>
                      <li>A walk at the busy Mall Road is another place for shopping and more so for one to taste various local dishes.</li>
                      <li>In the late evening you can go to the Old Manali to take the junk and feel the life of the cafes.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Manali to Solang Valley and Return to Manali</h4>
                    <ul>
                      <li>Post breakfast it will be a little drive to Solang valley for adventure activities.</li>
                      <li>Experience thrilling activities like zorbing, paragliding, and skiing (seasonal).</li>
                      <li>Enjoy the scenic beauty of the valley surrounded by snow-capped mountains.</li>
                      <li>Return to Manali in the evening for dinner and overnight stay.</li>
                      <li>Prepare for the trek to Kasol the next day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Manali to Kasol</h4>
                    <ul>
                      <li>Early morning departure from Manali to Kasol via the scenic route.</li>
                      <li>Pass through beautiful villages and enjoy the changing landscapes.</li>
                      <li>Arrive in Kasol and check into your accommodation.</li>
                      <li>Explore the local market and cafes in the evening.</li>
                      <li>Experience the vibrant culture of Kasol, often called the 'Amsterdam of India'.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Kasol Exploration and Trek to Chalal</h4>
                    <ul>
                      <li>Morning trek from Kasol to Chalal village, a beautiful 2-hour walk.</li>
                      <li>Pass through the Parvati River and enjoy the scenic beauty.</li>
                      <li>Visit the famous waterfall near Chalal village.</li>
                      <li>Explore the local culture and interact with villagers.</li>
                      <li>Return to Kasol in the evening for dinner and overnight stay.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Kasol to Manali</h4>
                    <ul>
                      <li>Morning departure from Kasol back to Manali.</li>
                      <li>Enjoy the return journey through the beautiful Parvati Valley.</li>
                      <li>Arrive in Manali and spend some time exploring or relaxing.</li>
                      <li>Evening departure from Manali for overnight journey to Delhi.</li>
                      <li>Reach Delhi by 06:00 AM next morning.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>All meals during the trek (breakfast, lunch, dinner)</li>
                    <li>Accommodation in hotels and guesthouses</li>
                    <li>Professional trek leader and support staff</li>
                    <li>All necessary permits and entry fees</li>
                    <li>First aid kit and emergency support</li>
                    <li>Transportation from Delhi to Manali and back</li>
                    <li>Local transportation during the trek</li>
                    <li>Adventure activities in Solang Valley</li>
                    <li>Safety equipment and communication devices</li>
                    <li>All sightseeing and trekking arrangements</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Personal expenses like shopping, laundry, etc.</li>
                    <li>Any kind of personal medication</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                    <li>Tips and gratuities</li>
                    <li>Travel insurance</li>
                    <li>Any optional activities not mentioned in the itinerary</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>5,180 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>540 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Delhi)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate between 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Mountain towns exploration</li>
                    <li>Cultural experiences</li>
                    <li>Scenic beauty</li>
                    <li>Adventure activities</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for all ages</li>
                    <li>Photography opportunities</li>
                    <li>Local market exploration</li>
                    <li>Professional guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default ManaliKasol;
